import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { Layers } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { getScoreBarColor } from "@/lib/chart";
import { cn, message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { SubBrandVoice } from "@/schema/brand-os/brand-intel";

function VoiceCard({ voice }: { voice: SubBrandVoice }) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-muted bg-muted px-5 py-3">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{voice.name}</p>
        {voice.tone && <p className="text-xs text-muted-foreground">{voice.tone}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Progress value={voice.alignment} className="h-2 flex-1 bg-secondary" indicatorClassName={getScoreBarColor(voice.alignment)} />
        <p className="shrink-0 text-xs text-muted-foreground">{voice.alignment} %</p>
      </div>
    </div>
  );
}

function SubBrandVoiceGrid({ voices }: { voices: SubBrandVoice[] }) {
  if (!voices.length) return <LockedContent message={message} />;
      return (
        <div className={cn("grid grid-cols-1 gap-4", voices.length === 1 ? "md:grid-cols-1" : "md:grid-cols-2")}>
          {voices.map((voice) => (
            <VoiceCard key={voice.name} voice={voice} />
          ))}
        </div>
      );
}

function SubBrandVoiceContent({ status, voices }: { status: QueryStatus; voices: SubBrandVoice[] }) {
  switch (status) {
    case "pending":
      return <VoicesSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return <SubBrandVoiceGrid voices={voices} />;
  }
}

export function SubBrandVoiceMapping() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchCreativeScores());

  const voices = (data?.subbrand_mapping ?? [])
    .filter((item) => item.average_score > 0)
    .map((item) => ({ name: item.name, alignment: Math.round(item.average_score) }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-blue-100/40 p-2">
          <Layers className="size-5 text-blue-600" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base font-semibold">Sub-Brand Voice Mapping</CardTitle>
      </CardHeader>
      <CardContent>
        <SubBrandVoiceContent status={status} voices={voices} />
      </CardContent>
    </Card>
  );
}

function VoicesSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex flex-col gap-4 rounded-lg border border-muted bg-muted px-5 py-3">
          <div className="space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-2 flex-1 rounded-full" />
            <Skeleton className="h-3 w-10 shrink-0" />
          </div>
        </div>
      ))}
    </div>
  );
}
